import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, PaperPlaneTilt, Eye, Users, Clock, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const auth = () => ({ withCredentials: true });

export const AdminCampaignsPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [skippedTest, setSkippedTest] = useState(0);
  const [selectedId, setSelectedId] = useState('');
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState({});
  const [confirmCount, setConfirmCount] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [loading, setLoading] = useState(true);

  // Custom-recipients mode: send to a specific pasted list instead of all real users.
  // Useful for targeted sends (one buddy, one beta tester) + "send test to myself" flow.
  const [customMode, setCustomMode] = useState(false);
  const [customEmails, setCustomEmails] = useState('');

  const parsedCustomEmails = useMemo(
    () => Array.from(new Set(
      customEmails
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
    )),
    [customEmails],
  );

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId),
    [templates, selectedId],
  );

  useEffect(() => {
    (async () => {
      try {
        const [tRes, rRes, hRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/campaigns/templates`, auth()),
          axios.get(`${API_URL}/api/admin/campaigns/recipients`, auth()),
          axios.get(`${API_URL}/api/admin/campaigns/history`, auth()),
        ]);
        setTemplates(tRes.data.templates);
        setRecipients(rRes.data.recipients);
        setSkippedTest(rRes.data.skipped_test_accounts);
        setHistory(hRes.data.summary || {});
        if (tRes.data.templates.length > 0) setSelectedId(tRes.data.templates[0].id);
      } catch (e) {
        toast.error(e.response?.status === 403 ? 'Admin only' : 'Could not load campaigns');
        navigate('/');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  useEffect(() => {
    if (!selectedId) return;
    setPreview(null);
    axios
      .get(`${API_URL}/api/admin/campaigns/preview/${selectedId}`, auth())
      .then((r) => setPreview(r.data))
      .catch(() => toast.error('Could not render preview'));
  }, [selectedId]);

  const alreadySent = history[selectedId]?.sent || 0;
  const allUsersEligible = Math.max(recipients.length - alreadySent, 0);
  const eligibleCount = customMode ? parsedCustomEmails.length : allUsersEligible;

  const onSend = async () => {
    if (parseInt(confirmCount, 10) !== eligibleCount) {
      toast.error(`Type ${eligibleCount} to confirm`);
      return;
    }
    const targetLabel = customMode
      ? `these ${eligibleCount} specific recipient(s):\n${parsedCustomEmails.join(', ')}`
      : `${eligibleCount} real users`;
    if (!window.confirm(`Send "${selected.subject}" to ${targetLabel}?\n\nThis is not a drill.`)) return;
    setSending(true);
    setSendResult(null);
    try {
      const payload = { template_id: selectedId, confirm_count: eligibleCount };
      if (customMode) payload.emails = parsedCustomEmails;
      const r = await axios.post(
        `${API_URL}/api/admin/campaigns/send`,
        payload,
        auth(),
      );
      setSendResult(r.data);
      toast.success(`Sent to ${r.data.sent} / ${r.data.attempted}`);
      // Refresh history
      const hRes = await axios.get(`${API_URL}/api/admin/campaigns/history`, auth());
      setHistory(hRes.data.summary || {});
      setConfirmCount('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const sendTestToMyself = async () => {
    if (!selected) return;
    try {
      // First pull my email from /auth/me to avoid guessing.
      const me = await axios.get(`${API_URL}/api/auth/me`, auth());
      const myEmail = me.data?.user?.email || me.data?.email;
      if (!myEmail) {
        toast.error('Could not resolve your admin email');
        return;
      }
      if (!window.confirm(`Send a test of "${selected.subject}" to ${myEmail}?`)) return;
      setSending(true);
      const r = await axios.post(
        `${API_URL}/api/admin/campaigns/send`,
        { template_id: selectedId, confirm_count: 1, emails: [myEmail] },
        auth(),
      );
      toast.success(`Test sent: ${r.data.sent}/${r.data.attempted}`);
      const hRes = await axios.get(`${API_URL}/api/admin/campaigns/history`, auth());
      setHistory(hRes.data.summary || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-campaigns-page">
      <SEO title="Campaigns — Admin" description="" noindex={true} />
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate('/admin')} className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="campaigns-back-btn">
            <ArrowLeft size={14} /> Back
          </button>
          <span className="text-sm font-bold">Email Campaigns</span>
          <span className="text-xs text-muted-foreground">admin only</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 grid gap-6 md:grid-cols-[380px_1fr]">
        {/* Left column — controls */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Users size={16} /> Recipients</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="text-3xl font-bold text-emerald-500" data-testid="campaign-real-count">{recipients.length}</div>
              <div className="text-xs text-muted-foreground">real users eligible</div>
              {skippedTest > 0 && (
                <div className="text-[11px] text-amber-500/80">
                  {skippedTest} test accounts auto-skipped
                </div>
              )}
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">view list</summary>
                <ul className="mt-2 space-y-1 max-h-48 overflow-auto" data-testid="campaign-recipient-list">
                  {recipients.map((r) => (
                    <li key={r.email} className="text-[11px] font-mono break-all">
                      {r.email}
                      {r.name && <span className="text-muted-foreground"> · {r.name}</span>}
                    </li>
                  ))}
                </ul>
              </details>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-sm">Template</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger data-testid="campaign-template-select">
                  <SelectValue placeholder="Pick a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selected && (
                <>
                  <div className="text-xs space-y-1">
                    <div><span className="text-muted-foreground">Subject:</span> {selected.subject}</div>
                    <div className="text-muted-foreground leading-snug">{selected.description}</div>
                  </div>
                  {alreadySent > 0 && (
                    <div className="flex items-center gap-2 text-xs text-emerald-500 bg-emerald-500/10 border border-emerald-500/20 rounded px-2 py-1.5">
                      <Clock size={12} /> Already sent to {alreadySent} — will be skipped
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card className="border-amber-500/40 bg-amber-500/5">
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><WarningCircle size={16} className="text-amber-500" /> Send</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {/* Mode toggle: all real users vs specific recipients */}
              <div className="flex rounded-md border border-border/60 overflow-hidden text-xs" data-testid="campaign-mode-toggle">
                <button
                  type="button"
                  onClick={() => { setCustomMode(false); setConfirmCount(''); }}
                  className={`flex-1 px-2 py-1.5 transition-colors ${!customMode ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 font-semibold' : 'text-muted-foreground hover:bg-muted/40'}`}
                  data-testid="campaign-mode-all"
                >
                  All real users ({allUsersEligible})
                </button>
                <button
                  type="button"
                  onClick={() => { setCustomMode(true); setConfirmCount(''); }}
                  className={`flex-1 px-2 py-1.5 transition-colors ${customMode ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 font-semibold' : 'text-muted-foreground hover:bg-muted/40'}`}
                  data-testid="campaign-mode-custom"
                >
                  Specific recipients
                </button>
              </div>

              {customMode && (
                <div>
                  <Label htmlFor="custom-emails" className="text-xs">Email addresses (comma, space, or newline separated)</Label>
                  <textarea
                    id="custom-emails"
                    value={customEmails}
                    onChange={(e) => setCustomEmails(e.target.value)}
                    placeholder="oalanis1@gmail.com"
                    rows={3}
                    disabled={sending}
                    data-testid="campaign-custom-emails"
                    className="w-full mt-1 px-3 py-2 text-sm font-mono rounded-md border border-border/60 bg-background focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  />
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Parsed: <span className="font-semibold text-foreground" data-testid="campaign-parsed-count">{parsedCustomEmails.length}</span> valid email{parsedCustomEmails.length === 1 ? '' : 's'}
                    {customEmails.trim() && parsedCustomEmails.length === 0 && ' (none valid yet)'}
                  </div>
                </div>
              )}

              <div className="text-xs text-muted-foreground leading-snug">
                Type <span className="font-bold text-foreground" data-testid="campaign-confirm-target">{eligibleCount}</span> below to confirm
                sending to <span className="font-bold text-foreground">{eligibleCount}</span> {customMode ? 'specific' : 'real'} recipient{eligibleCount === 1 ? '' : 's'}.
              </div>
              <div>
                <Label htmlFor="confirm" className="text-xs">Confirm count</Label>
                <Input
                  id="confirm"
                  type="number"
                  value={confirmCount}
                  onChange={(e) => setConfirmCount(e.target.value)}
                  placeholder={String(eligibleCount)}
                  disabled={sending || eligibleCount === 0}
                  data-testid="campaign-confirm-input"
                />
              </div>
              <Button
                onClick={onSend}
                disabled={sending || eligibleCount === 0 || parseInt(confirmCount, 10) !== eligibleCount}
                className="w-full"
                data-testid="campaign-send-btn"
              >
                <PaperPlaneTilt size={14} className="mr-1.5" />
                {sending ? 'Sending…' : `Send to ${eligibleCount}`}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={sendTestToMyself}
                disabled={sending || !selected}
                className="w-full"
                data-testid="campaign-send-test-btn"
              >
                Send test to myself first
              </Button>
              {sendResult && (
                <div className="text-xs space-y-1 pt-2 border-t border-border/40" data-testid="campaign-send-result">
                  <div className="flex items-center gap-2 text-emerald-500">
                    <CheckCircle size={12} /> Sent: {sendResult.sent} / {sendResult.attempted}
                  </div>
                  {sendResult.failed > 0 && (
                    <div className="flex items-center gap-2 text-rose-500">
                      <WarningCircle size={12} /> Failed: {sendResult.failed}
                    </div>
                  )}
                  {sendResult.errors?.slice(0, 5).map((er) => (
                    <div key={er.email} className="text-[10px] text-rose-500/80 font-mono break-all">{er.email}: {er.error}</div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column — preview */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><Eye size={16} /> Preview</CardTitle>
            {preview && <span className="text-xs text-muted-foreground font-mono">{preview.subject}</span>}
          </CardHeader>
          <CardContent>
            {preview ? (
              <iframe
                title="email-preview"
                srcDoc={preview.html}
                className="w-full h-[720px] bg-white rounded border border-border/40"
                data-testid="campaign-preview-iframe"
              />
            ) : (
              <div className="h-[400px] flex items-center justify-center text-muted-foreground text-sm">
                Select a template to preview
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminCampaignsPage;
